import * as React from 'react';

import { Heading, Tabs, Text } from '@/components/ui';

import DesignSystemComponents from './Components';
import ChartsFoundation from './foundations/Charts';
import ColorsFoundation from './foundations/Colors';
import RulesFoundation from './foundations/Rules';
import SpacingFoundation from './foundations/Spacing';
import TypographyFoundation from './foundations/Typography';

const TAB_IDS = ['colors', 'typography', 'spacing', 'charts', 'rules', 'components'];
const DEFAULT_TAB = 'components';

const parseTabFromHash = () => {
    const rawHash = window.location.hash.replace(/^#\/?/, '').trim();

    if (!rawHash) {
        return DEFAULT_TAB;
    }

    const firstSegment = rawHash.split('/')[0];

    return TAB_IDS.includes(firstSegment) ? firstSegment : DEFAULT_TAB;
};

const buildTabHash = tabId => `#/${tabId}`;

export default function DesignSystemPage() {
    const [activeTab, setActiveTab] = React.useState(parseTabFromHash);

    React.useEffect(() => {
        const handleHashChange = () => {
            setActiveTab(parseTabFromHash());
        };

        window.addEventListener('hashchange', handleHashChange);

        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    const handleTabChange = React.useCallback(tabId => {
        setActiveTab(tabId);

        const targetHash = buildTabHash(tabId);
        const currentHash = window.location.hash;

        // The Components tab owns its own sub-routing (#/components/{familyId}).
        // If the current hash is already anchored to a family inside the Components
        // tab, don't overwrite it when the user clicks the Components trigger —
        // otherwise we'd drop the deep-link.
        if (tabId === 'components' && currentHash.startsWith('#/components/')) {
            return;
        }

        if (currentHash !== targetHash) {
            window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${targetHash}`);
        }
    }, []);

    return (
        <div data-design-system-root className="tw-absolute tw-inset-0 tw-overflow-y-auto tw-bg-muted tw-text-foreground">
            <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-catalog-page tw-flex-col tw-gap-6 tw-p-6 lg:tw-gap-8 lg:tw-p-8">
                <header className="tw-space-y-1">
                    <Text tone="muted" variant="overline">WiseMetering UI</Text>
                    <Heading as="h1" variant="display">Design System</Heading>
                    <Text tone="muted" variant="body">
                        Tokens, primitives, and rules that keep the product visually consistent.
                    </Text>
                </header>

                <Tabs
                    items={[
                        { value: 'colors',     label: 'Colors',     content: <ColorsFoundation /> },
                        { value: 'typography', label: 'Typography', content: <TypographyFoundation /> },
                        { value: 'spacing',    label: 'Spacing',    content: <SpacingFoundation /> },
                        { value: 'charts',     label: 'Charts',     content: <ChartsFoundation /> },
                        { value: 'rules',      label: 'Rules',      content: <RulesFoundation /> },
                        { value: 'components', label: 'Components', content: <DesignSystemComponents /> }
                    ]}
                    onValueChange={handleTabChange}
                    value={activeTab}
                />
            </div>
        </div>
    );
}
